import React, {
  createContext,
  useContext,
  useCallback,
  useState,
  useEffect,
} from "react";
import { useParams } from "react-router-dom";
import { AuthContext } from "../../../../../../context/authContext";
import { getMcsStatusForTasks } from "./api";
import {
  getScheduleTasks,
  enrichTasksWithMcsStatus,
  buildProcurementProgressMapForContext,
  buildProcurementDatesMapForContext,
  buildProcurementCostMapForContext,
} from "./scheduleTaskApi";

/**
 * Context for Schedule4D (task-based rows with MCS status).
 * Loaded automatically when plantId is in the route; also set by Schedule4D
 * when that page loads. Consumed by MIS, PMS, and other modules so they can
 * use the same calculated schedule without re-fetching.
 *
 * Usage: Call useSchedule4D() to read scheduleMaterials (task rows), schedulePlantId,
 * procurementProgressMap, procurementCalculatedProgress.
 */
const Schedule4DContext = createContext({
  scheduleMaterials: [],
  schedulePlantId: null,
  scheduleLoading: false,
  setSchedule: () => {},
  procurementProgressMap: new Map(),
  procurementCalculatedProgress: null,
  procurementActualStartMap: new Map(),
  procurementActualEndMap: new Map(),
  procurementBudgetedCostMap: new Map(),
  procurementActualCostMap: new Map(),
});

export const useSchedule4D = () => useContext(Schedule4DContext);

/** Loads schedule data when plantId is available (from route params). Runs inside Provider. */
function Schedule4DDataLoader() {
  const { plantId } = useParams();
  const { accessToken } = useContext(AuthContext);
  const { setSchedule, setScheduleLoading } = useSchedule4D();

  useEffect(() => {
    if (!plantId) {
      setSchedule([], null);
      setScheduleLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setScheduleLoading(true);
      try {
        const [scheduleTasks, mcsStatus] = await Promise.all([
          getScheduleTasks({ plantId, accessToken }).catch(() => []),
          getMcsStatusForTasks(plantId).catch(() => ({
            byTaskCode: new Map(),
            byTaskId: new Map(),
          })),
        ]);
        if (cancelled) return;
        console.log("mcs status", mcsStatus);
        
        const merged = enrichTasksWithMcsStatus(scheduleTasks ?? [], mcsStatus);
        const { map: procurementProgressMap, avgProgress: procurementCalculatedProgress } =
          buildProcurementProgressMapForContext(merged);
        const { actualStartMap: procurementActualStartMap, actualEndMap: procurementActualEndMap } =
          buildProcurementDatesMapForContext(merged);
        const { budgetedCostMap: procurementBudgetedCostMap, actualCostMap: procurementActualCostMap } =
          buildProcurementCostMapForContext(merged);
        setSchedule(merged, plantId, {
          procurementProgressMap,
          procurementCalculatedProgress,
          procurementActualStartMap,
          procurementActualEndMap,
          procurementBudgetedCostMap,
          procurementActualCostMap,
        });
      } catch (err) {
        if (!cancelled) {
          console.error("[Schedule4DContext] Load failed:", err);
          setSchedule([], null);
        }
      } finally {
        if (!cancelled) setScheduleLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [plantId, accessToken, setSchedule, setScheduleLoading]);

  return null;
}

export function Schedule4DProvider({ children }) {
  const [scheduleMaterials, setScheduleMaterials] = useState([]);
  const [schedulePlantId, setSchedulePlantId] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [procurementProgressMap, setProcurementProgressMap] = useState(() => new Map());
  const [procurementCalculatedProgress, setProcurementCalculatedProgress] = useState(null);
  const [procurementActualStartMap, setProcurementActualStartMap] = useState(() => new Map());
  const [procurementActualEndMap, setProcurementActualEndMap] = useState(() => new Map());
  const [procurementBudgetedCostMap, setProcurementBudgetedCostMap] = useState(() => new Map());
  const [procurementActualCostMap, setProcurementActualCostMap] = useState(() => new Map());

  const setSchedule = useCallback(
    (materials, plantId, options = {}) => {
      setScheduleMaterials(Array.isArray(materials) ? materials : []);
      setSchedulePlantId(plantId ?? null);
      if (options.procurementProgressMap != null) {
        setProcurementProgressMap(
          options.procurementProgressMap instanceof Map
            ? new Map(options.procurementProgressMap)
            : new Map()
        );
      }
      if (options.procurementCalculatedProgress !== undefined) {
        setProcurementCalculatedProgress(options.procurementCalculatedProgress);
      }
      if (options.procurementActualStartMap != null) {
        setProcurementActualStartMap(
          options.procurementActualStartMap instanceof Map
            ? new Map(options.procurementActualStartMap)
            : new Map()
        );
      }
      if (options.procurementActualEndMap != null) {
        setProcurementActualEndMap(
          options.procurementActualEndMap instanceof Map
            ? new Map(options.procurementActualEndMap)
            : new Map()
        );
      }
      if (options.procurementBudgetedCostMap != null) {
        setProcurementBudgetedCostMap(
          options.procurementBudgetedCostMap instanceof Map
            ? new Map(options.procurementBudgetedCostMap)
            : new Map()
        );
      }
      if (options.procurementActualCostMap != null) {
        setProcurementActualCostMap(
          options.procurementActualCostMap instanceof Map
            ? new Map(options.procurementActualCostMap)
            : new Map()
        );
      }
    },
    []
  );

  const value = React.useMemo(
    () => ({
      scheduleMaterials,
      schedulePlantId,
      scheduleLoading,
      setSchedule,
      setScheduleLoading,
      procurementProgressMap,
      procurementCalculatedProgress,
      procurementActualStartMap,
      procurementActualEndMap,
      procurementBudgetedCostMap,
      procurementActualCostMap,
    }),
    [
      scheduleMaterials,
      schedulePlantId,
      scheduleLoading,
      setSchedule,
      setScheduleLoading,
      procurementProgressMap,
      procurementCalculatedProgress,
      procurementActualStartMap,
      procurementActualEndMap,
      procurementBudgetedCostMap,
      procurementActualCostMap,
    ]
  );

  return (
    <Schedule4DContext.Provider value={value}>
      <Schedule4DDataLoader />
      {children}
    </Schedule4DContext.Provider>
  );
}

export default Schedule4DContext;
